import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api, ApiError } from "../../../lib/api";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";

export type ExternalBarcodeData = {
  name: string;
  brand: string | null;
  image: string | null;
  category: string | null;
  barcode: string;
};

export type BarcodeResolveHandlers = {
  onLocalProduct: (p: { id: string; name: string }) => void | Promise<void>;
  onExternalProduct: (data: ExternalBarcodeData) => void | Promise<void>;
  /** When not found locally and not found on Open Food Facts */
  onNotFoundExternal: (barcode: string) => void | Promise<void>;
};

export function useBarcodeScanner(handlers: BarcodeResolveHandlers) {
  const { token } = useAuth();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const lastScanRef = useRef<{ barcode: string; atMs: number } | null>(null);
  const [resolving, setResolving] = useState(false);

  const resolveBarcode = useCallback(
    async (barcodeRaw: string) => {
      const barcode = barcodeRaw.trim();
      if (!barcode) return;
      if (!token) {
        toast.error("Not authenticated");
        return;
      }

      const now = Date.now();
      const last = lastScanRef.current;
      if (last && last.barcode === barcode && now - last.atMs < 500) {
        return;
      }
      lastScanRef.current = { barcode, atMs: now };

      const h = handlersRef.current;
      setResolving(true);
      try {
        const res = await api.products.resolveBarcode({ token, barcode });
        if (res.source === "local") {
          const p = res.data;
          await h.onLocalProduct({ id: String(p.id), name: p.name });
          return;
        }
        if (res.source === "external") {
          await h.onExternalProduct(res.data);
          return;
        }
      } catch (e) {
        if (
          e instanceof ApiError &&
          (e.code === "NOT_FOUND_EXTERNAL" || e.message === "NOT_FOUND_EXTERNAL" || e.code === "HTTP_404")
        ) {
          trackEvent(TRACKING_EVENTS.BARCODE_PRODUCT_NOT_FOUND, { barcode_length: barcode.length });
          await h.onNotFoundExternal(barcode);
          return;
        }
        trackEvent(TRACKING_EVENTS.BARCODE_FAILED);
        toast.error(e instanceof Error ? e.message : "Failed to resolve barcode");
      } finally {
        setResolving(false);
      }
    },
    [token],
  );

  return { resolveBarcode, resolving };
}
